import * as React from 'react';
import { Autocomplete } from 'Src/components/Autocomplete/Autocomplete';
import { getSchoolName, searchFunctionParticipantsOfTournament } from 'Src/helpers/autocomplete/autocomplete';
import { AppUser } from '../../../../App/App';
import { SchoolEvent, Participant } from 'Src/models/event';
import './TournamentEventAddSchoolForm.scss';

interface Props {
  event: SchoolEvent;
  user: AppUser;
  participants: Participant[];
  onCancel: () => void;
  onSubmit: (schoolIds: string[]) => void;
}

interface State {
  selectedSchools: any[];
}

export class TournamentEventAddSchoolForm extends React.Component<Props, State> {
  state: State = { selectedSchools: [] };

  getSchoolId = schoolParticipant =>
    schoolParticipant.schoolId ||
    (schoolParticipant.school && schoolParticipant.school.id) ||
    schoolParticipant.id ||
    '';

  getSchools = (text: string) => {
    const { user, event, participants } = this.props;
    const selectedIds = this.state.selectedSchools.map(this.getSchoolId);

    return searchFunctionParticipantsOfTournament(text, user, event, participants).then(schools =>
      schools.filter(school => !selectedIds.includes(this.getSchoolId(school)))
    );
  };

  renderSchoolOption = schoolParticipant => {
    const school = schoolParticipant && schoolParticipant.school ? schoolParticipant.school : schoolParticipant;
    const name = getSchoolName(schoolParticipant);
    const logo = school && (school.pic || school.logo || school.logoUrl);
    const postcode = school && school.postcode;

    return (
      <span className="tournamentEventAddSchoolForm__schoolOption">
        <span className="tournamentEventAddSchoolForm__schoolLogo" aria-hidden="true">
          {logo ? <img src={logo} alt="" /> : <span>{name ? name.charAt(0).toUpperCase() : 'S'}</span>}
        </span>
        <span className="tournamentEventAddSchoolForm__schoolDetails">
          <strong>{name}</strong>
          {postcode ? <small>{postcode}</small> : null}
        </span>
      </span>
    );
  };

  render() {
    const { onCancel, onSubmit } = this.props;
    const { selectedSchools } = this.state;

    return (
      <form
        className="tournamentEventAddSchoolForm"
        onSubmit={event => {
          event.preventDefault();
          if (selectedSchools.length) onSubmit(selectedSchools.map(this.getSchoolId));
        }}
      >
        <div className="tournamentEventAddSchoolForm__intro">
          <span aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M3 21h18M5 21V7l7-4 7 4v14M9 10h.01M15 10h.01M9 14h.01M15 14h.01M10 21v-4h4v4" />
            </svg>
          </span>
          <div>
            <strong>Add participating schools</strong>
            <p>Search and select one or more tournament schools. Schools already in this event are excluded.</p>
          </div>
        </div>

        <label className="tournamentEventAddSchoolForm__field">
          <span>
            Schools <i>*</i>
          </span>
          <Autocomplete
            key={`school-search-${selectedSchools.length}`}
            searchFunction={this.getSchools}
            getElementTitle={getSchoolName}
            customClass="mFullWidth tournamentEventAddSchoolForm__autocomplete"
            placeholder="Search by school name"
            renderItem={this.renderSchoolOption}
            isMenuPortal
            menuMaxHeight={260}
            onSelect={school => {
              const schoolId = this.getSchoolId(school);
              if (schoolId && !selectedSchools.some(item => this.getSchoolId(item) === schoolId)) {
                this.setState(state => ({ selectedSchools: [...state.selectedSchools, school] }));
              }
            }}
          />
        </label>

        {selectedSchools.length ? (
          <div className="tournamentEventAddSchoolForm__selected" aria-label="Selected schools">
            <header>
              <strong>Selected</strong>
              <span>{selectedSchools.length}</span>
            </header>
            {selectedSchools.map(schoolParticipant => {
              const school = schoolParticipant.school || schoolParticipant;
              const name = getSchoolName(schoolParticipant);
              const logo = school.pic || school.logo || school.logoUrl;
              const schoolId = this.getSchoolId(schoolParticipant);
              return (
                <div key={schoolId}>
                  <span aria-hidden="true">{logo ? <img src={logo} alt="" /> : name.charAt(0).toUpperCase()}</span>
                  <strong>{name}</strong>
                  <button
                    type="button"
                    aria-label={`Remove ${name}`}
                    onClick={() =>
                      this.setState(state => ({
                        selectedSchools: state.selectedSchools.filter(item => this.getSchoolId(item) !== schoolId)
                      }))
                    }
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        ) : null}

        <footer className="tournamentEventAddSchoolForm__actions">
          <button type="button" className="sit-btn sit-btn--secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="sit-btn sit-btn--primary" disabled={!selectedSchools.length}>
            Add {selectedSchools.length || ''} {selectedSchools.length === 1 ? 'school' : 'schools'}
          </button>
        </footer>
      </form>
    );
  }
}
